// Title badges as pictures (Oct 19 2026): one shared WebGL2 canvas draws each title's badge (src/kit/badgeGl.ts — the game's
// Unity badge material) at the size it is shown, and hands back a picture (a blob: URL), kept for the visit. The grey height
// map comes from the back end (images/titles/badges/<name>.png, through /api/title-art/badges/…); the ring atlas and the
// environment are the Unity app's own (public/badge/). Where WebGL2 isn't there, null: TitleBadge falls back to the game's
// flat coin pictures.
import { BadgeGl, envLevelsFrom, type EnvLevel } from "./badgeGl";

const NAME = /^[A-Za-z0-9_-]{1,120}$/;
export const badgeHeightUrl = (name: string): string | null => (NAME.test(name) ? `/api/title-art/badges/${name}.png` : null);

/** The size a badge is drawn at for a shown size (CSS px × device pixels), in steps so a few pictures serve every screen. */
export function badgePx(cssPx: number, dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1): number {
  const want = Math.max(32, Math.min(512, Math.ceil(cssPx * Math.min(dpr, 3))));
  return Math.min(512, Math.ceil(want / 32) * 32);
}

/** Tiers the badge material knows: 1–5 (more draws as 5, none as 1). */
export const badgeTier = (tier: number | null | undefined): number => Math.max(1, Math.min(5, Math.round(Number(tier) || 1)));

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((ok, no) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => ok(img);
    img.onerror = () => no(new Error(`couldn't load ${src}`));
    img.src = src;
  });

let assets: Promise<{ frame: HTMLImageElement; env: EnvLevel[] }> | null = null;
/** The ring atlas and the environment, loaded once. */
export function badgeAssets(): Promise<{ frame: HTMLImageElement; env: EnvLevel[] }> {
  if (!assets) {
    assets = Promise.all([
      loadImage("/badge/badge-covers.png"),
      fetch("/badge/badge-env.bin").then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error("badge environment")))),
    ]).then(([frame, buf]) => ({ frame, env: envLevelsFrom(buf) }));
    assets.catch(() => (assets = null));
  }
  return assets;
}

const heights = new Map<string, Promise<HTMLImageElement>>();
/** A title's height map, loaded once. */
export function badgeHeight(name: string): Promise<HTMLImageElement> {
  const url = badgeHeightUrl(name);
  if (!url) return Promise.reject(new Error("no such title"));
  let p = heights.get(name);
  if (!p) {
    p = loadImage(url);
    heights.set(name, p);
    p.catch(() => heights.delete(name));
  }
  return p;
}

let painter: Promise<BadgeGl | null> | null = null;
function sharedPainter(): Promise<BadgeGl | null> {
  if (!painter) {
    painter = badgeAssets()
      .then(({ frame, env }) => {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = 128;
        const gl = new BadgeGl(canvas, frame, env);
        canvas.addEventListener("webglcontextlost", () => (painter = null));
        return gl;
      })
      .catch((e) => {
        console.warn("title badges: drawing them isn't possible here", e);
        return null;
      });
  }
  return painter;
}

const pictures = new Map<string, Promise<string | null>>();
let queue: Promise<unknown> = Promise.resolve();

/** A title's badge at a tier and size, as a picture URL; null where it can't be drawn (no WebGL2, no height map). */
export function badgePicture(name: string, tier: number | null | undefined, px: number): Promise<string | null> {
  const t = badgeTier(tier);
  const key = `${name}|${t}|${px}`;
  let p = pictures.get(key);
  if (p) return p;
  p = Promise.all([sharedPainter(), badgeHeight(name).catch(() => null)]).then(([gl, height]) => {
    if (!gl || !height) return null;
    // one at a time: they share the canvas
    const job = queue.then(
      () =>
        new Promise<string | null>((done) => {
          try {
            gl.canvas.width = gl.canvas.height = px;
            gl.draw(height, t);
            (gl.canvas as HTMLCanvasElement).toBlob((b) => done(b ? URL.createObjectURL(b) : null), "image/png");
          } catch (e) {
            console.warn("title badge", name, e);
            done(null);
          }
        }),
    );
    queue = job.catch(() => null);
    return job;
  });
  pictures.set(key, p);
  p.then((v) => v === null && pictures.delete(key));
  return p;
}

// ------------------------------------------------------------------ tilt (BadgeTiltDriver, MainScreen.unity)

export const TILT = { strength: 1.5, maxTilt: 0.8, responsiveness: 12 } as const;

/** Clamps a tilt to the game's maximum (its length). */
export function clampTilt(x: number, y: number, max: number = TILT.maxTilt): [number, number] {
  const m = Math.hypot(x, y);
  return m > max ? [(x / m) * max, (y / m) * max] : [x, y];
}

/** The game's smoothing: frame-rate independent, toward the target. */
export function smoothTilt(cur: [number, number], target: [number, number], dtSec: number, k: number = TILT.responsiveness): [number, number] {
  const a = 1 - Math.exp(-k * Math.max(0, dtSec));
  return [cur[0] + (target[0] - cur[0]) * a, cur[1] + (target[1] - cur[1]) * a];
}

/** Phone tilt (deviceorientation beta/gamma, degrees) away from where it was first held → the game's tilt. The game flips x. */
export function tiltFromOrientation(beta: number, gamma: number, beta0: number, gamma0: number): [number, number] {
  const rad = Math.PI / 180;
  const x = -Math.sin((gamma - gamma0) * rad) * TILT.strength;
  const y = Math.sin((beta - beta0) * rad) * TILT.strength;
  return clampTilt(x, y);
}
